/**
 * Client-side server→client message bus (Phase 0). Shared by the student and
 * teacher apps.
 *
 * Transport-agnostic: `dispatchMessages` is the single entrypoint. Today it's
 * fed by the piggyback transport (the `server-messages` window event emitted
 * from shared-ui `handleResponse`); a future SSE/WebSocket would call the same
 * function. Handlers register by message `type` and are deduped by message id
 * (at-least-once delivery → idempotent handlers).
 *
 * See docs/FeatureDesigns/duels-invite-smoothing-plan.md.
 */

import type { ServerMessage } from '../api';

type MessageHandler = (msg: ServerMessage) => void;

const handlers = new Map<string, Set<MessageHandler>>();
const seen = new Set<string>();   // dedupe by message id

/** Register a handler for a message `type`. Returns an unsubscribe fn. */
export function registerMessageHandler(type: string, fn: MessageHandler): () => void {
  let set = handlers.get(type);
  if (!set) { set = new Set(); handlers.set(type, set); }
  set.add(fn);
  return () => { set!.delete(fn); };
}

/** Transport-agnostic dispatch. Deduped by id; unknown types are ignored. */
export function dispatchMessages(messages: ServerMessage[]): void {
  for (const m of messages) {
    if (!m?.id || seen.has(m.id)) continue;
    seen.add(m.id);
    const set = handlers.get(m.type);
    if (set) for (const fn of set) { try { fn(m); } catch { /* isolate handler errors */ } }
  }
  if (seen.size > 500) {
    // Trim oldest so the dedupe set stays bounded.
    const excess = seen.size - 500;
    let i = 0;
    for (const id of seen) { if (i++ >= excess) break; seen.delete(id); }
  }
}

let installed = false;
/** Wire the piggyback transport (window event) into the bus. Idempotent. */
export function installMessageBus(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('server-messages', (e: Event) => {
    const detail = (e as CustomEvent).detail as ServerMessage[] | undefined;
    if (Array.isArray(detail)) dispatchMessages(detail);
  });
}
